import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { usePointCloudUploadStatusLazyQuery, useProjectCalculationsLazyQuery, useProjectExportsLazyQuery } from '../../types/graphqlTypes';
import { pusher } from '../pusher';
import { useReactivePropertySync } from '../../modules/Properties/reactive';
import { useActionSync } from './useActionSync';
import { useAnnotationSync } from './useAnnotationSync';

export const useRealtimeUpdates = () => {
  const { projectId = '' } = useParams();
  const [fetchProjectCalculations] = useProjectCalculationsLazyQuery({ fetchPolicy: 'network-only' });
  const [fetchProject] = usePointCloudUploadStatusLazyQuery({ fetchPolicy: 'network-only' });
  const [fetchExports] = useProjectExportsLazyQuery({ fetchPolicy: 'network-only' });

  // Sync reactive properties with remote updates via Pusher
  // Uses unique clientId per browser session to filter out own updates
  useReactivePropertySync({ projectId });

  // Sync project actions (groups, layers, ordering) with remote updates via Pusher
  useActionSync({ projectId });

  // Sync annotation creation/deletion with remote updates via granular Pusher events
  useAnnotationSync({ projectId });

  useEffect(() => {
    const channel = pusher.subscribe(`project-${projectId}`);

    const handleCalculationDone = async () => {
      await fetchProjectCalculations({ variables: { projectId } });
    };

    const handleConvertDone = async () => {
      await fetchProject({ variables: { projectId } });
    };

    // Refresh exports list for UI updates (download is handled by useUserEvents)
    const handleExportDone = async () => {
      await fetchExports({ variables: { projectId } });
    };

    channel.bind('calculation-done', handleCalculationDone);
    channel.bind('convert-done', handleConvertDone);
    channel.bind('export-done', handleExportDone);

    return () => {
      channel.unbind('calculation-done', handleCalculationDone);
      channel.unbind('convert-done', handleConvertDone);
      channel.unbind('export-done', handleExportDone);
      pusher.unsubscribe(`project-${projectId}`);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);
};
